import React, { useState, useEffect, useCallback } from 'react';
import { useAppDispatch } from '../hooks/redux';
import { showToast } from '../features/ui/uiSlice';
import api from '../utils/axiosConfig';

interface TaskHierarchyData {
    task: {
        id: string;
        title: string;
        company: string;
        totalBudget: number;
        regions: string[];
    };
    regions: {
        [key: string]: {
            name: string;
            states: {
                [key: string]: {
                    name: string;
                    districts: {
                        [key: string]: {
                            name: string;
                            users: Array<{
                                id: string;
                                name: string;
                                designation: string;
                                status: string;
                                dataSubmitted: number;
                                earnings: number;
                            }>;
                            totalUsers: number;
                            totalEarnings: number;
                        };
                    };
                    totalUsers: number;
                    totalEarnings: number;
                };
            };
            totalUsers: number;
            totalEarnings: number;
        };
    };
}

interface TaskHierarchyViewProps {
    taskId: string;
}

const TaskHierarchyView: React.FC<TaskHierarchyViewProps> = ({ taskId }) => {
    const dispatch = useAppDispatch();
    const [hierarchyData, setHierarchyData] = useState<TaskHierarchyData | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
    const [selectedUser, setSelectedUser] = useState<any>(null);

    const fetchHierarchyData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await api.get(`/tasks/hierarchy/${taskId}`);
            setHierarchyData(response.data.data);
            // Expand all regions by default
            const regions = Object.keys(response.data.data.regions);
            setExpandedNodes(new Set(regions));
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch task hierarchy',
                type: 'error'
            }));
        } finally {
            setIsLoading(false);
        }
    }, [taskId, dispatch]);

    useEffect(() => {
        fetchHierarchyData();
    }, [fetchHierarchyData]);

    const toggleNode = (nodeId: string) => {
        const newExpanded = new Set(expandedNodes);
        if (newExpanded.has(nodeId)) {
            newExpanded.delete(nodeId);
        } else {
            newExpanded.add(nodeId);
        }
        setExpandedNodes(newExpanded);
    };

    const getDesignationColor = (designation: string) => {
        switch (designation) {
            case 'regional_head': return 'bg-purple-100 text-purple-800';
            case 'state_head': return 'bg-blue-100 text-blue-800';
            case 'district_head': return 'bg-green-100 text-green-800';
            case 'team_leader': return 'bg-yellow-100 text-yellow-800';
            case 'team_executive': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'bg-green-100 text-green-800';
            case 'accepted': return 'bg-blue-100 text-blue-800';
            case 'assigned': return 'bg-yellow-100 text-yellow-800';
            case 'rejected': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!hierarchyData) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-500">No hierarchy data available</p>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto p-6">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Task Hierarchy</h1>
                <div className="bg-white p-4 rounded-lg shadow">
                    <h2 className="text-xl font-semibold text-gray-900">{hierarchyData.task.title}</h2>
                    <p className="text-gray-600">Company: {hierarchyData.task.company}</p>
                    <p className="text-gray-600">Total Budget: ₹{hierarchyData.task.totalBudget?.toLocaleString() || '0'}</p>
                    <p className="text-gray-600">Regions: {hierarchyData.task.regions?.join(', ') || 'No regions'}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Hierarchy Tree */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                            <h2 className="text-lg font-medium text-gray-900">Team Hierarchy</h2>
                            <button
                                onClick={fetchHierarchyData}
                                disabled={isLoading}
                                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? 'Refreshing...' : 'Refresh'}
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="space-y-4">
                                {Object.entries(hierarchyData.regions).map(([regionKey, region]) => (
                                    <div key={regionKey} className="border border-gray-200 rounded-lg">
                                        <div
                                            className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
                                            onClick={() => toggleNode(regionKey)}
                                        >
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center">
                                                    <span className={`mr-2 ${expandedNodes.has(regionKey) ? 'transform rotate-90' : ''}`}>
                                                        ▶
                                                    </span>
                                                    <h3 className="text-lg font-medium text-gray-900 capitalize">{region.name}</h3>
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    {region.totalUsers} users • ₹{region.totalEarnings?.toLocaleString() || '0'}
                                                </div>
                                            </div>
                                        </div>

                                        {expandedNodes.has(regionKey) && (
                                            <div className="p-4 space-y-3">
                                                {Object.entries(region.states).map(([stateKey, state]) => (
                                                    <div key={stateKey} className="ml-4 border-l-2 border-gray-200 pl-4">
                                                        <div
                                                            className="p-3 bg-blue-50 cursor-pointer hover:bg-blue-100 rounded"
                                                            onClick={() => toggleNode(`${regionKey}-${stateKey}`)}
                                                        >
                                                            <div className="flex justify-between items-center">
                                                                <div className="flex items-center">
                                                                    <span className={`mr-2 ${expandedNodes.has(`${regionKey}-${stateKey}`) ? 'transform rotate-90' : ''}`}>
                                                                        ▶
                                                                    </span>
                                                                    <h4 className="font-medium text-gray-900">{state.name}</h4>
                                                                </div>
                                                                <div className="text-sm text-gray-600">
                                                                    {state.totalUsers} users • ₹{state.totalEarnings?.toLocaleString() || '0'}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {expandedNodes.has(`${regionKey}-${stateKey}`) && (
                                                            <div className="ml-4 space-y-2">
                                                                {Object.entries(state.districts).map(([districtKey, district]) => (
                                                                    <div key={districtKey} className="border border-gray-200 rounded p-3 bg-green-50">
                                                                        <div className="flex justify-between items-center mb-2">
                                                                            <h5 className="font-medium text-gray-900">{district.name}</h5>
                                                                            <div className="text-sm text-gray-600">
                                                                                {district.totalUsers} users • ₹{district.totalEarnings?.toLocaleString() || '0'}
                                                                            </div>
                                                                        </div>

                                                                        <div className="space-y-2">
                                                                            {district.users.map((user) => (
                                                                                <div
                                                                                    key={user.id}
                                                                                    className="p-2 bg-white rounded border cursor-pointer hover:bg-gray-50"
                                                                                    onClick={() => setSelectedUser(user)}
                                                                                >
                                                                                    <div className="flex justify-between items-center">
                                                                                        <div>
                                                                                            <p className="font-medium text-gray-900">{user.name}</p>
                                                                                            <div className="flex space-x-2 mt-1">
                                                                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDesignationColor(user.designation)}`}>
                                                                                                    {user.designation.replace('_', ' ')}
                                                                                                </span>
                                                                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(user.status)}`}>
                                                                                                    {user.status}
                                                                                                </span>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="text-right text-sm text-gray-600">
                                                                                            <p>{user.dataSubmitted} submissions</p>
                                                                                            <p>₹{user.earnings?.toLocaleString() || '0'}</p>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* User Details */}
                <div className="lg:col-span-1">
                    {selectedUser ? (
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-medium text-gray-900">User Details</h2>
                            </div>
                            <div className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Name</label>
                                    <p className="text-sm text-gray-900">{selectedUser.name}</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Designation</label>
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDesignationColor(selectedUser.designation)}`}>
                                        {selectedUser.designation.replace('_', ' ')}
                                    </span>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Status</label>
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedUser.status)}`}>
                                        {selectedUser.status}
                                    </span>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Data Submitted</label>
                                    <p className="text-sm text-gray-900">{selectedUser.dataSubmitted} submissions</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Earnings</label>
                                    <p className="text-sm text-gray-900">₹{selectedUser.earnings?.toLocaleString() || '0'}</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg shadow p-6 text-center">
                            <p className="text-gray-500">Select a user to view details</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Summary Statistics */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Total Users</h3>
                    <p className="text-3xl font-bold text-blue-600">
                        {Object.values(hierarchyData.regions).reduce((total, region) => total + region.totalUsers, 0)}
                    </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Total Earnings</h3>
                    <p className="text-3xl font-bold text-green-600">
                        ₹{Object.values(hierarchyData.regions).reduce((total, region) => total + (region.totalEarnings || 0), 0).toLocaleString()}
                    </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Active Regions</h3>
                    <p className="text-3xl font-bold text-purple-600">
                        {Object.keys(hierarchyData.regions).length}
                    </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Completion Rate</h3>
                    <p className="text-3xl font-bold text-orange-600">
                        {hierarchyData.task.totalBudget > 0
                            ? Math.round((Object.values(hierarchyData.regions).reduce((total, region) => total + region.totalEarnings, 0) / hierarchyData.task.totalBudget) * 100)
                            : 0}%
                    </p>
                </div>
            </div>
        </div>
    );
};

export default TaskHierarchyView;
